// CELL-BIO9 — optional WebGL barrel-distortion overlay.
// Creates a canvas overlay with a subtle CRT barrel warp.
// Only activates when body.crt is present AND the user hasn't set
// prefers-reduced-motion. Degrades silently if WebGL is unavailable.

const VERT_SRC = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

const FRAG_SRC = `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_tex;
uniform float u_strength;

vec2 barrel(vec2 uv, float k) {
  vec2 cc = uv - 0.5;
  float r2 = dot(cc, cc);
  return uv + cc * r2 * k;
}

void main() {
  vec2 warped = barrel(v_uv, u_strength);
  if (warped.x < 0.0 || warped.x > 1.0 || warped.y < 0.0 || warped.y > 1.0) {
    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }
  gl_FragColor = texture2D(u_tex, warped);
}
`;

export class WarpOverlay {
  private canvas: HTMLCanvasElement | null = null;
  private gl: WebGLRenderingContext | null = null;
  private program: WebGLProgram | null = null;
  private rafId = 0;
  private active = false;

  /** Attach the warp canvas overlay to the document body. */
  attach(): void {
    if (this.active) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const canvas = document.createElement("canvas");
    canvas.id = "warp-overlay";
    canvas.style.cssText =
      "position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:12;";
    document.body.appendChild(canvas);
    this.canvas = canvas;

    const gl = canvas.getContext("webgl");
    if (!gl) {
      canvas.remove();
      this.canvas = null;
      return;
    }
    this.gl = gl;

    if (!this.initGL()) {
      canvas.remove();
      this.canvas = null;
      this.gl = null;
      return;
    }

    this.active = true;
    this.resize();
    window.addEventListener("resize", () => this.resize());
    this.loop();
  }

  detach(): void {
    if (!this.active) return;
    cancelAnimationFrame(this.rafId);
    this.canvas?.remove();
    this.canvas = null;
    this.gl = null;
    this.active = false;
  }

  private initGL(): boolean {
    const gl = this.gl!;

    const vert = this.compileShader(gl.VERTEX_SHADER, VERT_SRC);
    const frag = this.compileShader(gl.FRAGMENT_SHADER, FRAG_SRC);
    if (!vert || !frag) return false;

    const prog = gl.createProgram();
    if (!prog) return false;
    gl.attachShader(prog, vert);
    gl.attachShader(prog, frag);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return false;
    this.program = prog;

    // Full-screen quad.
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    );
    const loc = gl.getAttribLocation(prog, "a_pos");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    // Texture placeholder for future screen-capture implementation.
    // Bind a 1×1 transparent pixel so the sampler is valid.
    const tex = gl.createTexture();
    if (!tex) return false;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
      new Uint8Array([0, 0, 0, 0]));
    // tex stays bound; no instance reference needed until screen-capture is wired.
    void tex;

    return true;
  }

  private compileShader(type: number, src: string): WebGLShader | null {
    const gl = this.gl!;
    const shader = gl.createShader(type);
    if (!shader) return null;
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      gl.deleteShader(shader);
      return null;
    }
    return shader;
  }

  private resize(): void {
    if (!this.canvas || !this.gl) return;
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
  }

  private loop(): void {
    this.rafId = requestAnimationFrame(() => this.loop());
    this.render();
  }

  private render(): void {
    const gl = this.gl;
    const prog = this.program;
    if (!gl || !prog || !this.canvas) return;

    // Lightweight barrel-warp pass on a transparent overlay.
    // The effect is subtle enough to work as a pure lens distortion on the
    // underlying DOM content without needing a full screen capture.
    // A full implementation would use html2canvas or OffscreenCanvas.
    gl.useProgram(prog);
    const strengthLoc = gl.getUniformLocation(prog, "u_strength");
    gl.uniform1f(strengthLoc, 0.08);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
}

export const warpOverlay = new WarpOverlay();
